import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { computePeacMonthly } from './peacLeasing';
import './ConfiguratorEpilab.css';

function ConfiguratorEpilab() {
  const [selectedMachine, setSelectedMachine] = useState('prime-s-unlimited');
  const [purchaseType, setPurchaseType] = useState('purchase');
  const [leaseDuration, setLeaseDuration] = useState('48');
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({
    installation: false,
    warranty: false,
    training: false
  });

  // Refs pour chaque section
  const modelSectionRef = useRef(null);
  const purchaseTypeSectionRef = useRef(null);
  const durationSectionRef = useRef(null);
  const optionsSectionRef = useRef(null);
  const packSectionRef = useRef(null);

  // Fonction pour scroller vers la section suivante
  const scrollToSection = (ref) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const media = [
    { type: 'video', url: 'https://www.youtube.com/embed/EqZbRTh89M0?si=UU0tAkEx6p1a1omE&start=0&autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3' },
    { type: 'image', url: 'http://medi-spa.fr/app/uploads/2025/11/epilab.jpg' },
    { type: 'image', url: 'https://images.unsplash.com/photo-1581093458791-9d0e0c8c553c?w=800&h=1200&fit=crop' },
    { type: 'image', url: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&h=1200&fit=crop' }
  ];

  const options = {
    installation: { name: 'Installation EPILAB', price: 490.00 },
    warranty: { name: 'Extension de garantie 5 ans', price: 5900.00 },
    training: { name: 'Formation à l\'appareil', price: 1690.00 }
  };

  const machines = {
    'prime-s-unlimited': {
      name: 'EPILAB Prime S Unlimited',
      description: 'Version compacte avec capacité illimitée',
      price: '28 750,00 €'
    },
    'prime-s-shot': {
      name: 'EPILAB Prime S Shot (30K inclus)',
      description: 'Version compacte avec 30 000 shots inclus',
      price: '24 150,00 €'
    },
    'prime-l-unlimited': {
      name: 'EPILAB Prime L Unlimited',
      description: 'Version large avec capacité illimitée',
      price: '34 500,00 €'
    },
    'prime-l-shot': {
      name: 'EPILAB Prime L Shot (30K inclus)',
      description: 'Version large avec 30 000 shots inclus',
      price: '28 750,00 €'
    }
  };

  const currentMachine = machines[selectedMachine];

  const parsePrice = (price) => parseFloat(price.replace(/\s/g, '').replace(',', '.').replace('€', ''));

  // Mensualité selon le barème PEAC, calculée sur le prix de la machine
  const getLeaseMonthly = (duration) => computePeacMonthly(parsePrice(currentMachine.price), parseInt(duration));

  const handleOptionToggle = (optionKey) => {
    // En location, les options sont incluses et ne peuvent pas être décochées
    if (purchaseType === 'lease') return;

    setSelectedOptions(prev => ({
      ...prev,
      [optionKey]: !prev[optionKey]
    }));

    scrollToSection(packSectionRef);
  };

  const calculateOptionsTotal = () => {
    if (purchaseType === 'lease') return 0;

    let total = 0;
    Object.keys(selectedOptions).forEach(key => {
      if (selectedOptions[key]) {
        total += options[key].price;
      }
    });
    return total;
  };

  const getBasePrice = () => {
    if (purchaseType === 'purchase') {
      return parsePrice(currentMachine.price);
    }
    return getLeaseMonthly(leaseDuration);
  };

  const formatPrice = (price) => {
    const [integerPart, decimalPart] = price.toFixed(2).split('.');
    // Ajouter des espaces pour les milliers
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formattedInteger},${decimalPart}`;
  };

  const getTotalPrice = () => {
    const basePrice = getBasePrice();
    const optionsTotal = calculateOptionsTotal();
    const total = basePrice + optionsTotal;

    if (purchaseType === 'purchase') {
      return formatPrice(total) + ' €';
    } else {
      return formatPrice(total) + ' €/mois';
    }
  };

  const getDisplayPrice = () => {
    return getTotalPrice();
  };

  return (
    <div className="configurator">
      {/* Bandeau promotionnel */}
      <div className="promo-banner">
        Disponible à partir de 449,28 €/mois
      </div>

      {/* Header avec retour */}
      <header className="configurator-header">
        <Link to="/" className="back-button">← Retour</Link>
        <h2>Configurateur EPILAB</h2>
      </header>

      {/* Layout principal */}
      <div className="configurator-layout">
        {/* Galerie d'images à gauche */}
        <div className="image-section">
          <div className="image-container">
            {media.map((item, index) => (
              item.type === 'video' ? (
                <iframe
                  key={index}
                  src={item.url}
                  title="EPILAB Video"
                  className={`machine-video ${index === currentImageIndex ? 'active' : ''}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <img
                  key={index}
                  src={item.url}
                  alt={`EPILAB ${index + 1}`}
                  className={`machine-image ${index === currentImageIndex ? 'active' : ''}`}
                />
              )
            ))}

            {/* Indicateurs de galerie */}
            <div className="gallery-indicators">
              {media.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Média ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Configurateur scrollable à droite */}
        <div className="config-section">
          <div className="config-content">
            {/* Section 1: Choix du modèle */}
            <section className="config-step" ref={modelSectionRef}>
              <h3 className="step-title">1. Choisissez votre modèle</h3>
              <div className="options-grid">
                {Object.entries(machines).map(([key, machine]) => (
                  <button
                    key={key}
                    className={`option-card ${selectedMachine === key ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedMachine(key);
                      scrollToSection(purchaseTypeSectionRef);
                    }}
                  >
                    <h4>{machine.name}</h4>
                    <p>{machine.description}</p>
                    <span className="option-price">{machine.price}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Section 2: Type d'acquisition */}
            <section className="config-step" ref={purchaseTypeSectionRef}>
              <h3 className="step-title">2. Mode d'acquisition</h3>
              <div className="toggle-group">
                <button
                  className={`toggle-button ${purchaseType === 'purchase' ? 'active' : ''}`}
                  onClick={() => {
                    setPurchaseType('purchase');
                    scrollToSection(optionsSectionRef);
                  }}
                >
                  <div className="toggle-icon">💳</div>
                  <div className="toggle-label">Achat</div>
                  <div className="toggle-description">Propriété immédiate</div>
                </button>
                <button
                  className={`toggle-button ${purchaseType === 'lease' ? 'active' : ''}`}
                  onClick={() => {
                    setPurchaseType('lease');
                    scrollToSection(durationSectionRef);
                  }}
                >
                  <div className="toggle-icon">📅</div>
                  <div className="toggle-label">Location</div>
                  <div className="toggle-description">Flexibilité financière</div>
                </button>
              </div>
            </section>

            {/* Section 3: Durée de location (conditionnelle) */}
            {purchaseType === 'lease' && (
              <section className="config-step lease-duration" ref={durationSectionRef}>
                <h3 className="step-title">3. Durée de location</h3>
                <div className="toggle-group">
                  <button
                    className={`toggle-button ${leaseDuration === '48' ? 'active' : ''}`}
                    onClick={() => {
                      setLeaseDuration('48');
                      scrollToSection(optionsSectionRef);
                    }}
                  >
                    <div className="toggle-label">48 mois</div>
                    <div className="toggle-price">{formatPrice(getLeaseMonthly('48'))} €/mois</div>
                    <div className="toggle-description">4 ans de location</div>
                  </button>
                  <button
                    className={`toggle-button ${leaseDuration === '60' ? 'active' : ''}`}
                    onClick={() => {
                      setLeaseDuration('60');
                      scrollToSection(optionsSectionRef);
                    }}
                  >
                    <div className="toggle-label">60 mois</div>
                    <div className="toggle-price">{formatPrice(getLeaseMonthly('60'))} €/mois</div>
                    <div className="toggle-description">5 ans de location</div>
                  </button>
                </div>
              </section>
            )}

            {/* Section 4: Options */}
            <section className="config-step" ref={optionsSectionRef}>
              <h3 className="step-title">
                {purchaseType === 'lease' ? '3. Options incluses' : '3. Options'}
              </h3>
              <div className="options-list">
                {Object.entries(options).map(([key, option]) => (
                  <div
                    key={key}
                    className={`option-item ${purchaseType === 'lease' ? 'included' : ''} ${
                      selectedOptions[key] || purchaseType === 'lease' ? 'selected' : ''
                    }`}
                    onClick={() => handleOptionToggle(key)}
                  >
                    <div className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={purchaseType === 'lease' || selectedOptions[key]}
                        disabled={purchaseType === 'lease'}
                        onChange={() => {}}
                      />
                    </div>
                    <div className="option-info">
                      <div className="option-name">{option.name}</div>
                      <div className="option-price-container">
                        <div className="option-price">{formatPrice(option.price)} €</div>
                        {purchaseType === 'lease' && (
                          <div className="option-included-badge">Inclus</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 5: Pack accessoires offert */}
            <section className="config-step pack-accessories" ref={packSectionRef}>
              <div className="pack-header">
                <h3 className="step-title">Pack Accessoires</h3>
                <div className="pack-badge-offered">OFFERT</div>
              </div>
              <div className="pack-content">
                <ul className="pack-items-list">
                  <li>Lunettes opérateur EPILAB</li>
                  <li>Lunettes client EPILAB</li>
                  <li>Protection hygiène /100</li>
                  <li>Liquide froid BC4</li>
                  <li>Spray laser skin care 250ml</li>
                  <li>Bidon de gel echo 5L</li>
                </ul>
                <div className="pack-value">
                  <span className="pack-value-label">Valeur:</span>
                  <span className="pack-value-price">749,90 €</span>
                </div>
              </div>
            </section>

            {/* Espace pour scroll */}
            <div style={{ height: '100px' }}></div>
          </div>
        </div>
      </div>

      {/* Résumé sticky en bas */}
      <div className="config-summary-sticky">
        <div className="summary-bar" onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}>
          <div className="summary-main">
            <span className="summary-price-label">
              {purchaseType === 'purchase' ? 'Prix total' : 'Mensualité'}
            </span>
            <span className="summary-price-value">{getDisplayPrice()}</span>
          </div>
          <button className="expand-toggle">
            <span className={`arrow ${isDetailsExpanded ? 'up' : 'down'}`}>▼</span>
          </button>
        </div>

        {isDetailsExpanded && (
          <div className="summary-details-expanded">
            <div className="summary-item">
              <span className="summary-label">Modèle:</span>
              <span className="summary-value">{currentMachine.name}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Mode:</span>
              <span className="summary-value">
                {purchaseType === 'purchase' ? 'Achat' : 'Location'}
              </span>
            </div>
            {purchaseType === 'lease' && (
              <div className="summary-item">
                <span className="summary-label">Durée:</span>
                <span className="summary-value">{leaseDuration} mois</span>
              </div>
            )}
            {purchaseType === 'purchase' && calculateOptionsTotal() > 0 && (
              <div className="summary-item">
                <span className="summary-label">Options:</span>
                <span className="summary-value">
                  {formatPrice(calculateOptionsTotal())} €
                </span>
              </div>
            )}
            {purchaseType === 'lease' && (
              <div className="summary-item">
                <span className="summary-label">Options:</span>
                <span className="summary-value">Incluses</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfiguratorEpilab;
